import React, { useState, useEffect } from "react";
import { AdminSideMenu } from "../admin/AdminMenu";
import API_URL from "../../api/API";
import { useAdminAuthContext } from "../admin/useAdminAuthContext";

export const AdminStoreManage = () => {
  const { admin } = useAdminAuthContext();

  const [storeData, setStoreData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    latitude: "",
    longitude: "",
    geofenceRadius: 500,
    isActive: true,
  });

  // FETCH ALL STORES
  const fetchStoreData = async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await fetch(API_URL.ADMIN_MANAGE_STORE, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${admin.token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch stores");
      }

      setStoreData(data.stores);
      setFilteredData(data.stores);

      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  // FORM INPUT CHANGE
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // OPEN CREATE FORM
  const handleCreate = () => {
    setIsEditing(false);
    setSelectedStoreId(null);

    setFormData({
      name: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      latitude: "",
      longitude: "",
      geofenceRadius: 500,
      isActive: true,
    });

    setError("");
    setShowForm(true);
  };

  // OPEN EDIT FORM
  const handleEdit = (store) => {
    setIsEditing(true);
    setSelectedStoreId(store._id);

    setFormData({
      name: store.name || "",
      address: store.address || "",
      city: store.city || "",
      state: store.state || "",
      zipCode: store.zipCode || "",
      latitude: store.latitude ?? "",
      longitude: store.longitude ?? "",
      geofenceRadius: store.geofenceRadius ?? 500,
      isActive: store.isActive ?? true,
    });

    setError("");
    setShowForm(true);
  };

  // CREATE / UPDATE STORE
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSaving(true);
      setError("");

      const url = isEditing
        ? API_URL.ADMIN_EDIT_STORE + selectedStoreId
        : API_URL.ADMIN_CREATE_STORE;

      const method = isEditing ? "POST" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${admin.token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          latitude: Number(formData.latitude),
          longitude: Number(formData.longitude),
          geofenceRadius: Number(formData.geofenceRadius),
          isActive: formData.isActive,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to save store");
      }

      alert(
        isEditing
          ? "Store updated successfully!"
          : "Store created successfully!"
      );

      setShowForm(false);
      setIsEditing(false);
      setSelectedStoreId(null);

      fetchStoreData();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // DELETE STORE
  const deleteStore = async (id) => {
    try {
      setIsDeleting(true);
      setError("");

      const response = await fetch(
        API_URL.ADMIN_DELETE_STORE + id,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${admin.token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to delete store");
      }

      alert("Store deleted successfully!");

      fetchStoreData();
    } catch (err) {
      alert(`Error deleting store: ${err.message}`);
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // DELETE CONFIRMATION
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this store?")) {
      deleteStore(id);
    }
  };

  // SEARCH
  const handleSearch = (query) => {
    setSearchQuery(query);

    if (query.trim() === "") {
      setFilteredData(storeData);
      return;
    }

    const lowerQuery = query.toLowerCase();

    const filtered = storeData.filter(
      (store) =>
        store.name?.toLowerCase().includes(lowerQuery) ||
        store.address?.toLowerCase().includes(lowerQuery) ||
        store.city?.toLowerCase().includes(lowerQuery) ||
        store.state?.toLowerCase().includes(lowerQuery) ||
        store.zipCode?.toLowerCase().includes(lowerQuery)
    );

    setFilteredData(filtered);
  };

  useEffect(() => {
    if (admin?.token) {
      fetchStoreData();
    }
  }, [admin]);

  return (
    <div className="flex h-screen">
      <AdminSideMenu />

      <div className="flex-1 p-6 bg-gray-100 pt-16 flex flex-col">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">
            Manage Stores
          </h1>

          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-600"
          >
            Create Store
          </button>
        </div>

        {/* SEARCH */}
        <div className="flex justify-end mb-4">
          <input
            type="text"
            placeholder="Search by store name, address, city, state or ZIP"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-1/3 p-2 border border-gray-300 rounded"
          />
        </div>

        {/* ERROR */}
        {error && !showForm && (
          <div className="mb-4 p-3 bg-red-100 text-red-600 rounded">
            {error}
          </div>
        )}

        {/* STORE TABLE */}
        <div className="flex-1 overflow-auto border rounded shadow bg-white">

          <table className="table-auto w-full">

            <thead className="sticky top-0 bg-gray-200">
              <tr>
                <th className="px-4 py-2 text-left">
                  Store Name
                </th>

                <th className="px-4 py-2 text-left">
                  Address
                </th>

                <th className="px-4 py-2 text-left">
                  City
                </th>

                <th className="px-4 py-2 text-left">
                  State
                </th>

                <th className="px-4 py-2 text-left">
                  ZIP
                </th>

                <th className="px-4 py-2 text-left">
                  Geofence
                </th>

                <th className="px-4 py-2 text-left">
                  Status
                </th>

                <th className="px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>

              {isLoading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <p className="text-blue-600">
                      Loading stores...
                    </p>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <p>No stores found</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((store, index) => (
                  <tr
                    key={store._id}
                    className={
                      index % 2 === 0
                        ? "bg-white hover:bg-gray-100 border-b border-gray-200"
                        : "bg-gray-50 hover:bg-gray-100 border-b border-gray-200"
                    }
                  >

                    <td className="px-4 py-2">
                      {store.name}
                    </td>

                    <td className="px-4 py-2">
                      {store.address}
                    </td>

                    <td className="px-4 py-2">
                      {store.city}
                    </td>

                    <td className="px-4 py-2">
                      {store.state}
                    </td>

                    <td className="px-4 py-2">
                      {store.zipCode}
                    </td>

                    <td className="px-4 py-2">
                      {store.geofenceRadius} m
                    </td>

                    <td className="px-4 py-2">
                      {store.isActive ? (
                        <span className="text-green-600 font-medium">
                          Active
                        </span>
                      ) : (
                        <span className="text-red-600 font-medium">
                          Inactive
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-2">

                      <button
                        onClick={() => handleEdit(store)}
                        className="px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 mr-2"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => handleDelete(store._id)}
                        className="px-2 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                        disabled={isDeleting}
                      >
                        Delete
                      </button>

                    </td>

                  </tr>
                ))
              )}

            </tbody>

          </table>

        </div>

        {/* CREATE / EDIT FORM */}

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">

            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">

              <div className="flex justify-between items-center mb-5">

                <h2 className="text-xl font-bold">
                  {isEditing ? "Edit Store" : "Create Store"}
                </h2>

                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-500 hover:text-gray-800 text-xl"
                >
                  ✕
                </button>

              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-600 rounded">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  {/* STORE NAME */}

                  <div className="md:col-span-2">
                    <label className="block mb-1 font-medium">
                      Store Name
                    </label>

                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>

                  {/* ADDRESS */}

                  <div className="md:col-span-2">
                    <label className="block mb-1 font-medium">
                      Address
                    </label>

                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>

                  {/* CITY */}

                  <div>
                    <label className="block mb-1 font-medium">
                      City
                    </label>

                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>

                  {/* STATE */}

                  <div>
                    <label className="block mb-1 font-medium">
                      State
                    </label>

                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>

                  {/* ZIP */}

                  <div>
                    <label className="block mb-1 font-medium">
                      ZIP Code
                    </label>

                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>

                  {/* LATITUDE */}

                  <div>
                    <label className="block mb-1 font-medium">
                      Latitude
                    </label>

                    <input
                      type="number"
                      step="any"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>

                  {/* LONGITUDE */}

                  <div>
                    <label className="block mb-1 font-medium">
                      Longitude
                    </label>

                    <input
                      type="number"
                      step="any"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>

                  {/* GEOFENCE */}

                  <div>
                    <label className="block mb-1 font-medium">
                      Geofence Radius (meters)
                    </label>

                    <input
                      type="number"
                      min="1"
                      name="geofenceRadius"
                      value={formData.geofenceRadius}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>

                  {/* ACTIVE */}

                  <div className="flex items-center mt-6">

                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="mr-2"
                    />

                    <label className="font-medium">
                      Active Store
                    </label>

                  </div>

                </div>

                {/* FORM BUTTONS */}

                <div className="flex justify-end mt-6 gap-3">

                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-600"
                    disabled={isSaving}
                  >
                    {isSaving
                      ? "Saving..."
                      : isEditing
                      ? "Update Store"
                      : "Create Store"}
                  </button>

                </div>

              </form>

            </div>

          </div>
        )}

      </div>
    </div>
  );
};